import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ExternalLink, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Convert Google Drive URLs to reliable thumbnail URLs
const convertGoogleDriveUrl = (url) => {
  if (!url) return '/placeholder.jpg';
  if (url.includes('drive.google.com/thumbnail') || url.includes('lh3.googleusercontent.com')) return url;
  
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w800`;
  
  const openMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (openMatch) return `https://drive.google.com/thumbnail?id=${openMatch[1]}&sz=w800`;
  
  const ucMatch = url.match(/drive\.google\.com\/uc\?(?:export=view&)?id=([a-zA-Z0-9_-]+)/);
  if (ucMatch) return `https://drive.google.com/thumbnail?id=${ucMatch[1]}&sz=w800`;
  
  return url;
};

export const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(`${API_URL}/api/projects`);
        if (response.ok) {
          const data = await response.json();
          setProjects(data);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="projects-page">
      {/* Hero Section */}
      <section className="editorial-container py-12 lg:py-20 border-b border-border">
        <div className="max-w-4xl">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold editorial-serif mb-6 leading-tight">
            Projects
          </h1>
          <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-2xl">
            Active IP within the ecosystem. Each project generates momentum, validates the system, 
            and compounds learnings.
          </p>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="editorial-container py-12 lg:py-16">
        {projects.length === 0 ? (
          <p className="text-center text-muted-foreground">No projects found.</p>
        ) : (
          <div className="grid gap-12 lg:gap-16">
            {projects.map((project, index) => (
              <div 
                key={project.project_id} 
                className={index !== 0 ? 'pt-12 lg:pt-16 border-t border-border' : ''}
                data-testid={`project-card-${project.slug}`}
              >
                <Card className="overflow-hidden border-0 shadow-none group">
                  <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
                    {/* Image */}
                    <div className="lg:col-span-2">
                      <Link to={`/project/${project.slug}`}>
                        <div className="editorial-image-hover relative aspect-[4/3] lg:aspect-square bg-black overflow-hidden">
                          <img
                            src={convertGoogleDriveUrl(project.image)}
                            alt={project.title}
                            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                          />
                        </div>
                      </Link>
                    </div>

                    {/* Content */}
                    <div className="lg:col-span-3 flex flex-col">
                      <div className="mb-4">
                        {project.status && (
                          <Badge className="bg-primary text-primary-foreground mb-3">
                            {project.status}
                          </Badge>
                        )}
                        <Link to={`/project/${project.slug}`}>
                          <h2 className="text-3xl lg:text-4xl font-bold editorial-serif mb-2 group-hover:accent-red transition-colors">
                            {project.title}
                          </h2>
                        </Link>
                        {(project.tagline || project.logline) && (
                          <p className="text-lg text-muted-foreground italic">
                            {project.tagline || project.logline}
                          </p>
                        )}
                      </div>

                      {(project.synopsis || project.description) && (
                        <p className="text-base text-muted-foreground leading-relaxed mb-6">
                          {project.synopsis || project.description}
                        </p>
                      )}

                      {/* Type */}
                      {project.type && (
                        <div className="mb-6">
                          <span className="text-xs px-3 py-1 border border-border text-muted-foreground uppercase">
                            {project.type}
                          </span>
                        </div>
                      )}

                      {/* Links */}
                      <div className="flex flex-wrap gap-3 mt-auto">
                        <Link to={`/project/${project.slug}`}>
                          <Button variant="default" data-testid={`view-project-${project.slug}`}>
                            View Project
                            <ArrowRight size={16} className="ml-2" />
                          </Button>
                        </Link>
                        {project.website && (
                          <a href={project.website} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline">
                              Official Site
                              <ExternalLink size={16} className="ml-2" />
                            </Button>
                          </a>
                        )}
                        {project.imdb && (
                          <a href={project.imdb} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost">
                              IMDb
                              <ExternalLink size={16} className="ml-2" />
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="editorial-container py-16 lg:py-24 border-t border-border">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold editorial-serif mb-4">
            Explore the System
          </h2>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
            See how companies and creators align to make these projects sustainable.
          </p>
          <Link to="/companies">
            <Button size="lg" data-testid="view-companies-btn">
              View Companies
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
